import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useConfirmDialog, useNoticeDialog } from './ConfirmDialog';
import { useToast } from './Toast';
import { getApiUrl } from '../config/api';
import { PROMPT_LIBRARY, type PromptLibraryEntry } from '../prompts/promptLibrary';

export type PromptGalleryAgentPick = { id: string; name: string };

type CommunityPromptApi = {
  id: string;
  title: string;
  body: string;
  tags: string[];
  suggested_agent_ids: string[];
  author_email?: string;
  source?: string;
};

type MinePromptApi = {
  id: string;
  pk: string;
  sk: string;
  title: string;
  body: string;
  tags: string[];
  suggested_agent_ids: string[];
  share_scope: string;
  mod_pk: string;
  author_email: string;
  review_note?: string;
  shared_by_email?: string;
  source_pk?: string;
  source_sk?: string;
  deleted_at?: string | null;
  expires_at?: number | null;
};

export type UnifiedGalleryEntry = PromptLibraryEntry & {
  source: 'official' | 'community' | 'mine';
  pk?: string;
  sk?: string;
  mod_pk?: string;
  share_scope?: string;
  shared_by_email?: string;
};

export type GallerySourceFilter = 'all' | UnifiedGalleryEntry['source'];

function gallerySourceLabel(source: UnifiedGalleryEntry['source']): string {
  if (source === 'official') return 'Official';
  if (source === 'community') return 'Community';
  return 'My prompt';
}

/** Badge on prompt cards (Browse). */
function gallerySourceChipClasses(source: UnifiedGalleryEntry['source'], theme: 'dark' | 'light'): string {
  const base = 'text-[10px] px-2 py-0.5 rounded shrink-0 border font-medium';
  if (theme === 'dark') {
    if (source === 'official') return `${base} bg-sky-900/55 text-sky-100 border-sky-500/45`;
    if (source === 'community') return `${base} bg-violet-900/55 text-violet-100 border-violet-500/45`;
    return `${base} bg-emerald-900/55 text-emerald-100 border-emerald-500/45`;
  }
  if (source === 'official') return `${base} bg-sky-100 text-sky-900 border-sky-300`;
  if (source === 'community') return `${base} bg-violet-100 text-violet-900 border-violet-300`;
  return `${base} bg-emerald-100 text-emerald-900 border-emerald-300`;
}

/** Selected state for Source filter chips. */
function gallerySourceFilterSelectedClass(id: GallerySourceFilter, theme: 'dark' | 'light'): string {
  if (id === 'all') {
    return theme === 'dark'
      ? 'bg-cyan-600/25 text-cyan-200 border-cyan-500/50'
      : 'bg-cyan-100 text-cyan-900 border-cyan-400';
  }
  if (theme === 'dark') {
    if (id === 'official') return 'bg-sky-800/70 text-sky-50 border-sky-400/60';
    if (id === 'community') return 'bg-violet-800/70 text-violet-50 border-violet-400/60';
    return 'bg-emerald-800/70 text-emerald-50 border-emerald-400/60';
  }
  if (id === 'official') return 'bg-sky-300 text-sky-950 border-sky-500';
  if (id === 'community') return 'bg-violet-300 text-violet-950 border-violet-500';
  return 'bg-emerald-300 text-emerald-950 border-emerald-500';
}

const SOURCE_FILTER_OPTIONS: { id: GallerySourceFilter; label: string }[] = [
  { id: 'all', label: 'All sources' },
  { id: 'official', label: 'Official' },
  { id: 'community', label: 'Community' },
  { id: 'mine', label: 'My prompts' },
];

type Props = {
  open: boolean;
  onClose: () => void;
  theme: 'dark' | 'light';
  agentsForPicker: PromptGalleryAgentPick[];
  allowedAgentIds: Set<string> | null;
  onUsePrompt: (body: string, agent: PromptGalleryAgentPick) => void;
  accessToken: string;
  /** From `GET …/api/config` — `loading` until fetch settles so we do not confuse with missing `PROMPTS_TABLE_NAME`. */
  promptsConfigFetch: 'loading' | 'ok' | 'error';
  promptsTableConfigured: boolean;
};

function entryVisibleForUser(entry: Pick<PromptLibraryEntry, 'suggestedAgentIds'>, allowed: Set<string>): boolean {
  return entry.suggestedAgentIds.some((id) => allowed.has(id));
}

function pickAgentForEntry(
  entry: Pick<PromptLibraryEntry, 'suggestedAgentIds'>,
  agentsForPicker: PromptGalleryAgentPick[],
): PromptGalleryAgentPick | null {
  const byId = new Map(agentsForPicker.map((a) => [a.id, a]));
  for (const id of entry.suggestedAgentIds) {
    const hit = byId.get(id);
    if (hit) return hit;
  }
  return null;
}

function mapCommunityToEntry(c: CommunityPromptApi): PromptLibraryEntry {
  return {
    id: c.id,
    title: c.title,
    body: c.body,
    tags: Array.isArray(c.tags) ? c.tags : [],
    suggestedAgentIds: Array.isArray(c.suggested_agent_ids) ? c.suggested_agent_ids : [],
  };
}

function mapMinePersonalToEntry(m: MinePromptApi): PromptLibraryEntry | null {
  if (m.share_scope !== 'personal' && m.share_scope !== 'peer_received') return null;
  return {
    id: m.id,
    title: m.title,
    body: m.body,
    tags: Array.isArray(m.tags) ? m.tags : [],
    suggestedAgentIds: Array.isArray(m.suggested_agent_ids) ? m.suggested_agent_ids : [],
  };
}

export const PromptGalleryModal: React.FC<Props> = ({
  open,
  onClose,
  theme,
  agentsForPicker,
  allowedAgentIds,
  onUsePrompt,
  accessToken,
  promptsConfigFetch,
  promptsTableConfigured,
}) => {
  const { confirm: confirmAction, dialog: confirmDialog } = useConfirmDialog(theme);
  const { showNotice, dialog: noticeDialog } = useNoticeDialog(theme);
  const { show: showToast, toast: toastPortal } = useToast();
  const promptCloudReady = promptsConfigFetch === 'ok' && promptsTableConfigured;
  const [tab, setTab] = useState<'browse' | 'add' | 'mine'>('browse');
  const [mineView, setMineView] = useState<'active' | 'deleted'>('active');
  const [query, setQuery] = useState('');
  const [tagFilter, setTagFilter] = useState<string | null>(null);
  const [agentFilter, setAgentFilter] = useState<string>('all');
  const [sourceFilter, setSourceFilter] = useState<GallerySourceFilter>('all');
  const [community, setCommunity] = useState<CommunityPromptApi[]>([]);
  const [mine, setMine] = useState<MinePromptApi[]>([]);
  const [deletedMine, setDeletedMine] = useState<MinePromptApi[]>([]);
  const [retentionDays, setRetentionDays] = useState(180);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [loadingData, setLoadingData] = useState(false);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [formTitle, setFormTitle] = useState('');
  const [formBody, setFormBody] = useState('');
  const [formTags, setFormTags] = useState('');
  const [formAgents, setFormAgents] = useState<Record<string, boolean>>({});
  const [formShare, setFormShare] = useState<'personal' | 'global_request'>('personal');
  const [editing, setEditing] = useState<MinePromptApi | null>(null);
  const [sharingPromptId, setSharingPromptId] = useState<string | null>(null);
  const [shareInput, setShareInput] = useState('');
  const [shareRecipients, setShareRecipients] = useState<string[]>([]);
  const [shareStatus, setShareStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [shareError, setShareError] = useState<string | null>(null);
  const [colleagueEmails, setColleagueEmails] = useState<string[]>([]);
  const [colleagueEmailsLoaded, setColleagueEmailsLoaded] = useState(false);
  const [shareDropdownOpen, setShareDropdownOpen] = useState(false);
  const [shareDropdownIdx, setShareDropdownIdx] = useState(-1);
  const shareInputRef = useRef<HTMLInputElement>(null);
  const shareDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Only true while the modal is open; used so we reset Browse/filters once per open, not on token/agent-list churn. */
  const galleryOpenSessionRef = useRef(false);

  // Track viewport size so the modal can shrink its chrome at low resolutions (~1280x800 and below).
  const [viewportSize, setViewportSize] = useState({ width: window.innerWidth, height: window.innerHeight });
  useEffect(() => {
    const handleResize = () => setViewportSize({ width: window.innerWidth, height: window.innerHeight });
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  const isCompact = viewportSize.width <= 1280 || viewportSize.height <= 800;

  const allowed = useMemo(() => {
    if (allowedAgentIds && allowedAgentIds.size > 0) return allowedAgentIds;
    return new Set(agentsForPicker.map((a) => a.id));
  }, [allowedAgentIds, agentsForPicker]);

  const authHeaders = useCallback(() => {
    const h: Record<string, string> = { Accept: 'application/json' };
    if (accessToken.trim()) h.Authorization = `Bearer ${accessToken.trim()}`;
    return h;
  }, [accessToken]);

  const reloadRemote = useCallback(async (silent = false) => {
    setLoadErr(null);
    if (!silent) setLoadingData(true);
    try {
      if (!accessToken.trim()) {
        setCommunity([]);
        setMine([]);
        setDeletedMine([]);
        return;
      }
      const headers = authHeaders();
      const [libR, mineR, deletedR] = await Promise.all([
        fetch(getApiUrl('/prompts/library'), { headers }),
        fetch(getApiUrl('/prompts/mine'), { headers }),
        fetch(getApiUrl('/prompts/deleted'), { headers }),
      ]);
      const libJ = await libR.json().catch(() => ({}));
      const mineJ = await mineR.json().catch(() => ({}));
      const deletedJ = await deletedR.json().catch(() => ({}));
      if (libR.ok && Array.isArray(libJ.community)) {
        setCommunity(libJ.community as CommunityPromptApi[]);
      } else {
        setCommunity([]);
        if (libR.status === 401 || libR.status === 403) {
          setLoadErr(typeof libJ.detail === 'string' ? libJ.detail : 'Could not load community prompts (sign in required).');
        }
      }
      if (mineR.ok && Array.isArray(mineJ.entries)) {
        setMine(mineJ.entries as MinePromptApi[]);
      } else {
        setMine([]);
        if (mineR.status === 401 || mineR.status === 403) {
          setLoadErr(typeof mineJ.detail === 'string' ? mineJ.detail : 'Could not load your prompts (sign in required).');
        }
      }
      if (deletedR.ok && Array.isArray(deletedJ.entries)) {
        setDeletedMine(deletedJ.entries as MinePromptApi[]);
        if (typeof deletedJ.retention_days === 'number' && deletedJ.retention_days > 0) {
          setRetentionDays(deletedJ.retention_days);
        }
      } else {
        setDeletedMine([]);
      }
    } catch (e) {
      setLoadErr(e instanceof Error ? e.message : 'Network error');
    } finally {
      if (!silent) setLoadingData(false);
    }
  }, [accessToken, authHeaders]);

  useEffect(() => {
    if (!open) {
      galleryOpenSessionRef.current = false;
      return;
    }
    if (!galleryOpenSessionRef.current) {
      galleryOpenSessionRef.current = true;
      setQuery('');
      setTagFilter(null);
      setAgentFilter('all');
      setSourceFilter('all');
      setTab('browse');
      setMineView('active');
      setEditing(null);
      setFormTitle('');
      setFormBody('');
      setFormTags('');
      setFormShare('personal');
      setSharingPromptId(null);
      setShareInput('');
      setShareRecipients([]);
      setShareStatus('idle');
      setShareError(null);
      setShareDropdownOpen(false);
      setShareDropdownIdx(-1);
      const initAgents: Record<string, boolean> = {};
      for (const a of agentsForPicker) initAgents[a.id] = false;
      if (agentsForPicker[0]) initAgents[agentsForPicker[0].id] = true;
      setFormAgents(initAgents);
      window.clarity?.('event', 'prompt_gallery_opened');
    }
    void reloadRemote();
  }, [open, agentsForPicker, reloadRemote]);

  useEffect(() => {
    if (!editing) return;
    setFormTitle(editing.title);
    setFormBody(editing.body);
    setFormTags((editing.tags || []).join(', '));
    setFormShare(editing.share_scope === 'global_request' ? 'global_request' : 'personal');
    const ag: Record<string, boolean> = {};
    for (const a of agentsForPicker) ag[a.id] = editing.suggested_agent_ids?.includes(a.id) ?? false;
    if (!Object.values(ag).some(Boolean) && agentsForPicker[0]) ag[agentsForPicker[0].id] = true;
    setFormAgents(ag);
  }, [editing, agentsForPicker]);

  const mergedBrowseEntries: UnifiedGalleryEntry[] = useMemo(() => {
    const official: UnifiedGalleryEntry[] = PROMPT_LIBRARY.filter((e) => entryVisibleForUser(e, allowed)).map(
      (e) => ({ ...e, source: 'official' as const }),
    );
    const fromCommunity: UnifiedGalleryEntry[] = community
      .map((c) => ({ ...mapCommunityToEntry(c), source: 'community' as const }))
      .filter((e) => entryVisibleForUser(e, allowed));
    const fromMine: UnifiedGalleryEntry[] = mine
      .map((m): UnifiedGalleryEntry | null => {
        const e = mapMinePersonalToEntry(m);
        if (!e) return null;
        return {
          ...e,
          source: 'mine' as const,
          pk: m.pk,
          sk: m.sk,
          mod_pk: m.mod_pk,
          share_scope: m.share_scope,
          shared_by_email: m.shared_by_email,
        };
      })
      .filter((x): x is UnifiedGalleryEntry => x !== null && entryVisibleForUser(x, allowed));
    return [...official, ...fromCommunity, ...fromMine];
  }, [allowed, community, mine]);

  const allTags = useMemo(() => {
    const s = new Set<string>();
    for (const e of mergedBrowseEntries) for (const t of e.tags) s.add(t);
    return Array.from(s).sort((a, b) => a.localeCompare(b));
  }, [mergedBrowseEntries]);

  const agentOptions = useMemo(() => {
    const ids = new Set<string>();
    for (const e of mergedBrowseEntries) for (const id of e.suggestedAgentIds) if (allowed.has(id)) ids.add(id);
    return agentsForPicker.filter((a) => ids.has(a.id));
  }, [mergedBrowseEntries, agentsForPicker, allowed]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return mergedBrowseEntries.filter((e) => {
      if (sourceFilter !== 'all' && e.source !== sourceFilter) return false;
      if (tagFilter && !e.tags.includes(tagFilter)) return false;
      if (agentFilter !== 'all' && !e.suggestedAgentIds.includes(agentFilter)) return false;
      if (!q) return true;
      const hay = `${e.title}\n${e.body}\n${e.tags.join(' ')}`.toLowerCase();
      return hay.includes(q);
    });
  }, [mergedBrowseEntries, query, tagFilter, agentFilter, sourceFilter]);

  const selectedAgentIds = () => Object.entries(formAgents).filter(([, v]) => v).map(([k]) => k);

  const submitForm = async () => {
    if (!promptCloudReady) return;
    const tags = formTags
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean);
    const agents = selectedAgentIds();
    setSaving(true);
    setLoadErr(null);
    try {
      const payload = {
        title: formTitle.trim(),
        body: formBody.trim(),
        tags,
        suggested_agent_ids: agents,
        share_scope: formShare,
      };
      const url = getApiUrl('/prompts');
      const init: RequestInit = editing
        ? {
          method: 'PUT',
          headers: { ...authHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify({
            pk: editing.pk,
            sk: editing.sk,
            ...payload,
          }),
        }
        : {
          method: 'POST',
          headers: { ...authHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        };
      const r = await fetch(url, init);
      const j = await r.json().catch(() => ({}));
      if (!r.ok) {
        const d = j.detail;
        const msg = typeof d === 'string' ? d : `Save failed (${r.status})`;
        setLoadErr(msg);
        showToast(msg, 'error');
        window.clarity?.('event', 'prompt_gallery_persist_failure');
        window.clarity?.('set', 'gallery_persist_failure_type', editing ? 'update' : 'save');
        return;
      }
      if (editing) {
        showToast(
          formShare === 'global_request'
            ? 'Prompt updated and submitted for community review.'
            : 'Prompt updated.',
          'success',
        );
      } else {
        showToast(
          formShare === 'global_request'
            ? 'Prompt saved and submitted for community review.'
            : 'Prompt saved to My prompts.',
          'success',
        );
      }
      window.clarity?.('event', editing ? 'prompt_gallery_prompt_updated' : 'prompt_gallery_prompt_saved');
      window.clarity?.('set', 'gallery_share_scope', formShare);
      setEditing(null);
      setFormTitle('');
      setFormBody('');
      setFormTags('');
      setFormShare('personal');
      const initAgents: Record<string, boolean> = {};
      for (const a of agentsForPicker) initAgents[a.id] = false;
      if (agentsForPicker[0]) initAgents[agentsForPicker[0].id] = true;
      setFormAgents(initAgents);
      setTab('mine');
      await reloadRemote(true);
    } finally {
      setSaving(false);
    }
  };

  const deleteMine = async (row: MinePromptApi) => {
    const isApprovedCommunity =
      row.share_scope === 'global_request' && String(row.mod_pk || '').toUpperCase() === 'APPROVED';
    if (isApprovedCommunity) {
      showNotice(
        'This prompt is published in the community catalog and cannot be deleted from your library. Please contact an administrator to retire it.',
        'Cannot delete published prompt',
        'Got it',
        'warning',
      );
      return;
    }

    const isPersonal = row.share_scope === 'personal';
    if (
      !(await confirmAction({
        title: isPersonal ? 'Move to Deleted' : 'Delete prompt',
        message: isPersonal
          ? `Move this prompt to Deleted? You can restore it within ${retentionDays} days, or permanently delete it from the Deleted view.`
          : 'Delete this saved prompt? This cannot be undone.',
        confirmLabel: isPersonal ? 'Move to Deleted' : 'Delete',
        variant: 'danger',
      }))
    ) {
      return;
    }
    setBusyId(row.sk);
    setLoadErr(null);
    setMine((prev) => prev.filter((x) => x.sk !== row.sk));
    try {
      const q = new URLSearchParams({ pk: row.pk, sk: row.sk });
      const r = await fetch(`${getApiUrl('/prompts')}?${q}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) {
        setMine((prev) => [row, ...prev.filter((x) => x.sk !== row.sk)]);
        const d = j.detail;
        const msg = typeof d === 'string' ? d : `Delete failed (${r.status})`;
        setLoadErr(msg);
        showToast(msg, 'error');
        window.clarity?.('event', 'prompt_gallery_persist_failure');
        window.clarity?.('set', 'gallery_persist_failure_type', 'delete');
        return;
      }
      showToast(
        isPersonal ? 'Prompt moved to Deleted.' : 'Prompt deleted.',
        'success',
      );
      window.clarity?.('event', isPersonal ? 'prompt_gallery_prompt_soft_deleted' : 'prompt_gallery_prompt_deleted');
      if (editing?.sk === row.sk) setEditing(null);
      void reloadRemote(true);
    } finally {
      setBusyId(null);
    }
  };

  const restoreMine = async (row: MinePromptApi) => {
    setBusyId(row.sk);
    setLoadErr(null);
    setDeletedMine((prev) => prev.filter((x) => x.sk !== row.sk));
    try {
      const r = await fetch(getApiUrl('/prompts/restore'), {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ pk: row.pk, sk: row.sk }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) {
        setDeletedMine((prev) => [row, ...prev.filter((x) => x.sk !== row.sk)]);
        const d = j.detail;
        const msg = typeof d === 'string' ? d : `Restore failed (${r.status})`;
        setLoadErr(msg);
        showToast(msg, 'error');
        return;
      }
      showToast('Prompt restored to Active.', 'success');
      window.clarity?.('event', 'prompt_gallery_prompt_restored');
      void reloadRemote(true);
    } finally {
      setBusyId(null);
    }
  };

  const purgeMine = async (row: MinePromptApi) => {
    if (
      !(await confirmAction({
        title: 'Permanently delete',
        message: 'Permanently delete this prompt? This cannot be undone.',
        confirmLabel: 'Permanently delete',
        variant: 'danger',
      }))
    ) {
      return;
    }
    setBusyId(row.sk);
    setLoadErr(null);
    setDeletedMine((prev) => prev.filter((x) => x.sk !== row.sk));
    try {
      const r = await fetch(getApiUrl('/prompts/purge'), {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ pk: row.pk, sk: row.sk }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) {
        setDeletedMine((prev) => [row, ...prev.filter((x) => x.sk !== row.sk)]);
        const d = j.detail;
        const msg = typeof d === 'string' ? d : `Permanent delete failed (${r.status})`;
        setLoadErr(msg);
        showToast(msg, 'error');
        return;
      }
      showToast('Prompt permanently deleted.', 'success');
      window.clarity?.('event', 'prompt_gallery_prompt_purged');
      void reloadRemote(true);
    } finally {
      setBusyId(null);
    }
  };

  const daysLeftHint = (row: MinePromptApi): string | null => {
    if (typeof row.expires_at === 'number' && row.expires_at > 0) {
      const ms = row.expires_at * 1000 - Date.now();
      const days = Math.max(0, Math.ceil(ms / (24 * 60 * 60 * 1000)));
      return days <= 1 ? 'Expires within 1 day' : `Expires in ~${days} days`;
    }
    return `Retained up to ${retentionDays} days`;
  };

  const dismissShared = async (row: MinePromptApi) => {
    if (
      !(await confirmAction({
        title: 'Dismiss shared prompt',
        message: 'Remove this shared prompt from your gallery?',
        confirmLabel: 'Dismiss',
        variant: 'danger',
      }))
    ) {
      return;
    }
    setBusyId(row.sk);
    setLoadErr(null);
    setMine((prev) => prev.filter((x) => x.sk !== row.sk));
    try {
      const q = new URLSearchParams({ pk: row.pk, sk: row.sk });
      const r = await fetch(`${getApiUrl('/prompts')}?${q}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) {
        setMine((prev) => [row, ...prev.filter((x) => x.sk !== row.sk)]);
        const d = j.detail;
        const msg = typeof d === 'string' ? d : `Dismiss failed (${r.status})`;
        setLoadErr(msg);
        showToast(msg, 'error');
        return;
      }
      showToast('Shared prompt dismissed.', 'success');
      window.clarity?.('event', 'prompt_gallery_shared_dismissed');
      void reloadRemote(true);
    } finally {
      setBusyId(null);
    }
  };

  const saveShared = async (row: MinePromptApi) => {
    if (!promptCloudReady) return;
    setSavingId(row.sk);
    setLoadErr(null);
    try {
      const payload = {
        title: row.title,
        body: row.body,
        tags: Array.isArray(row.tags) ? row.tags : [],
        suggested_agent_ids: Array.isArray(row.suggested_agent_ids) ? row.suggested_agent_ids : [],
        share_scope: 'personal',
      };
      const saveR = await fetch(getApiUrl('/prompts'), {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const saveJ = await saveR.json().catch(() => ({}));
      if (!saveR.ok) {
        const d = saveJ.detail;
        const msg = typeof d === 'string' ? d : `Save failed (${saveR.status})`;
        setLoadErr(msg);
        showToast(msg, 'error');
        window.clarity?.('event', 'prompt_gallery_persist_failure');
        window.clarity?.('set', 'gallery_persist_failure_type', 'save_shared');
        return;
      }
      // Delete the shared record from the server
      const q = new URLSearchParams({ pk: row.pk, sk: row.sk });
      await fetch(`${getApiUrl('/prompts')}?${q}`, { method: 'DELETE', headers: authHeaders() });
      // Convert the card in-place: replace the peer_received entry with the new personal one
      const newItem: MinePromptApi = saveJ.entry ?? saveJ.prompt ?? {
        ...row,
        pk: saveJ.pk ?? row.pk,
        sk: saveJ.sk ?? row.sk,
        share_scope: 'personal',
        shared_by_email: undefined,
        source_pk: undefined,
        source_sk: undefined,
      };
      setMine((prev) => prev.map((x) => (x.sk === row.sk ? newItem : x)));
      showToast('Saved a personal copy of this prompt.', 'success');
      window.clarity?.('event', 'prompt_gallery_shared_saved');
      void reloadRemote(true);
    } finally {
      setSavingId(null);
    }
  };

  const loadColleagues = useCallback(async () => {
    if (colleagueEmailsLoaded || !accessToken.trim()) return;
    try {
      const r = await fetch(getApiUrl('/colleagues'), { headers: authHeaders() });
      const j = await r.json().catch(() => ({}));
      if (r.ok && Array.isArray(j.emails)) {
        setColleagueEmails(j.emails as string[]);
      }
    } catch {
      // non-fatal: fall back to free-form entry
    } finally {
      setColleagueEmailsLoaded(true);
    }
  }, [colleagueEmailsLoaded, accessToken, authHeaders]);

  const openSharePanel = (promptId: string) => {
    setSharingPromptId((cur) => (cur === promptId ? null : promptId));
    setShareInput('');
    setShareRecipients([]);
    setShareStatus('idle');
    setShareError(null);
    setShareDropdownOpen(false);
    setShareDropdownIdx(-1);
    void loadColleagues();
  };

  const shareDropdownSuggestions = useMemo(() => {
    const q = shareInput.trim().toLowerCase();
    if (!q) return [];
    return colleagueEmails
      .filter((e) => e.includes(q) && !shareRecipients.includes(e))
      .slice(0, 8);
  }, [shareInput, colleagueEmails, shareRecipients]);

  const addShareRecipient = (email: string) => {
    const e = email.trim().toLowerCase();
    if (!e || !e.includes('@')) return;
    setShareRecipients((prev) => (prev.includes(e) ? prev : [...prev, e]));
    setShareInput('');
    setShareError(null);
    setShareDropdownOpen(false);
    setShareDropdownIdx(-1);
    shareInputRef.current?.focus();
  };

  const removeShareRecipient = (email: string) => {
    setShareRecipients((prev) => prev.filter((e) => e !== email));
  };

  const submitShare = async (row: MinePromptApi) => {
    if (shareRecipients.length === 0) return;
    setShareStatus('sending');
    setShareError(null);
    try {
      const r = await fetch(getApiUrl('/prompts/share'), {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ pk: row.pk, sk: row.sk, recipient_emails: shareRecipients }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) {
        const d = j.detail;
        const msg = typeof d === 'string' ? d : `Share failed (${r.status})`;
        setShareStatus('error');
        setShareError(msg);
        showToast(msg, 'error');
        window.clarity?.('event', 'prompt_gallery_persist_failure');
        window.clarity?.('set', 'gallery_persist_failure_type', 'share');
        return;
      }
      const results: { email: string; ok: boolean; error?: string }[] = j.results ?? [];
      const failed = results.filter((x) => !x.ok);
      if (failed.length > 0) {
        setShareStatus('error');
        const communityShareBlocked = failed.every(
          (f) => (f.error ?? '').toLowerCase().includes('only personal prompts can be shared'),
        );
        const errMsg = communityShareBlocked
          ? 'This prompt is already shared with the community, so it can’t be shared with colleagues.'
          : failed.map((f) => `${f.email}: ${f.error ?? 'failed'}`).join(' | ');
        setShareError(errMsg);
        showToast(errMsg, 'error');
        window.clarity?.('event', 'prompt_gallery_persist_failure');
        window.clarity?.('set', 'gallery_persist_failure_type', 'share_partial');
      } else {
        setShareStatus('sent');
        showToast(
          `Shared with ${shareRecipients.length} colleague${shareRecipients.length !== 1 ? 's' : ''}.`,
          'success',
        );
        window.clarity?.('event', 'prompt_gallery_prompt_shared');
        window.clarity?.('set', 'gallery_share_recipient_count', String(shareRecipients.length));
      }
    } catch (e) {
      setShareStatus('error');
      const msg = e instanceof Error ? e.message : 'Network error';
      setShareError(msg);
      showToast(msg, 'error');
    }
  };

  const modLabel = (m: MinePromptApi) => {
    if (m.share_scope === 'personal') return 'Personal';
    if (m.share_scope === 'peer_received') return 'Shared with you';
    if (m.mod_pk === 'PENDING') return 'Pending review';
    if (m.mod_pk === 'APPROVED') return 'Published';
    if (m.mod_pk === 'REJECTED') return 'Rejected';
    return m.mod_pk;
  };

  if (!open) return null;

  const overlay = theme === 'dark' ? 'bg-black bg-opacity-50' : 'bg-gray-900 bg-opacity-30';
  const panel = theme === 'dark' ? 'bg-gray-800' : 'bg-white';
  const composerBorder =
    theme === 'dark'
      ? 'linear-gradient(90deg, #0099FF 0%, #47C7B2 50%, #C5E654 100%)'
      : '#e5e7eb';
  const textPrimary = theme === 'dark' ? 'text-white' : 'text-gray-900';
  const textSecondary = theme === 'dark' ? 'text-gray-300' : 'text-gray-700';
  const textMuted = theme === 'dark' ? 'text-gray-400' : 'text-gray-500';
  const inputBg = theme === 'dark' ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900';
  const cardBorder = theme === 'dark' ? 'border-gray-600 hover:border-cyan-500/40' : 'border-gray-200 hover:border-cyan-500/50';
  const chipOff = theme === 'dark' ? 'bg-gray-700 text-gray-200' : 'bg-gray-100 text-gray-800';
  const chipOn = 'bg-cyan-600/20 text-cyan-300 border border-cyan-500/40';

  const tabBtn = (id: typeof tab, label: string, icon: React.ReactNode) => (
    <button
      key={id}
      type="button"
      onClick={() => {
        setTab(id);
        window.clarity?.('event', 'prompt_gallery_tab_switched');
        window.clarity?.('set', 'gallery_tab', id);
      }}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all duration-150 whitespace-nowrap ${
        tab === id
          ? 'bg-cyan-600 text-white shadow-sm'
          : `${textSecondary} ${theme === 'dark' ? 'hover:bg-gray-700 hover:text-white' : 'hover:bg-gray-100 hover:text-gray-900'}`
      }`}
    >
      {icon}
      {label}
    </button>
  );

  return createPortal(
    <>
      <style>{`
        @keyframes pgm-loading-bar {
          0%   { transform: translateX(-100%) scaleX(0.3); }
          50%  { transform: translateX(0%)    scaleX(0.7); }
          100% { transform: translateX(100%)  scaleX(0.3); }
        }
      `}</style>
      <div
        className={`fixed inset-0 ${overlay} flex items-center justify-center z-[9999] overflow-y-auto ${isCompact ? 'p-2' : 'p-4'} [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-gray-400/40`}
      >
        <div
          className="rounded-xl shadow-xl max-w-5xl w-full"
          style={{
            background: composerBorder,
            padding: '1px',
            height: isCompact ? 'min(720px, calc(100vh - 1rem))' : 'min(900px, 90vh)',
          }}
        >
        <div className={`rounded-xl w-full h-full flex flex-col overflow-hidden ${panel}`}>
          <div className={`flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 ${isCompact ? 'p-3 gap-2' : 'p-5'} border-b ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
            <div>
              <h2 className={`${isCompact ? 'text-lg' : 'text-xl'} font-semibold ${textPrimary}`}>CW AI Prompt Gallery</h2>
              {!isCompact && (
                <p className={`text-sm mt-1 ${textMuted}`}>
                  Official starters, your saves, and community prompts (after admin approval).
                </p>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <div className={`flex items-center gap-1 rounded-xl p-1 border ${theme === 'dark' ? 'bg-gray-900/70 border-gray-700/50' : 'bg-gray-100 border-gray-200'}`}>
                {tabBtn('browse',
                  'Browse',
                  <svg className="w-3.5 h-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
                  </svg>,
                )}
                {tabBtn('add',
                  'Add prompt',
                  <svg className="w-3.5 h-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                  </svg>,
                )}
                {tabBtn('mine',
                  'My prompts',
                  <svg className="w-3.5 h-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M17.982 18.725A7.488 7.488 0 0 0 12 15.75a7.488 7.488 0 0 0-5.982 2.975m11.963 0a9 9 0 1 0-11.963 0m11.963 0A8.966 8.966 0 0 1 12 21a8.966 8.966 0 0 1-5.982-2.275M15 9.75a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                  </svg>,
                )}
                <button
                  type="button"
                  title="Refresh gallery"
                  disabled={loadingData || refreshing}
                  onClick={async () => {
                    setRefreshing(true);
                    await reloadRemote();
                    setRefreshing(false);
                  }}
                  className={`inline-flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-lg transition-all duration-150 disabled:cursor-not-allowed whitespace-nowrap ${
                    refreshing
                      ? 'bg-cyan-600 text-white shadow-sm'
                      : `${theme === 'dark' ? 'text-gray-400 hover:bg-gray-700 hover:text-white' : 'text-gray-500 hover:bg-white hover:text-gray-800'}`
                  }`}
                >
                  <svg
                    className={`w-3.5 h-3.5 shrink-0 ${refreshing ? 'animate-spin' : ''}`}
                    fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99" />
                  </svg>
                  Refresh
                </button>
              </div>
              <button
                type="button"
                onClick={() => {
                  onClose();
                  window.clarity?.('event', 'prompt_gallery_closed');
                }}
                className={`p-2 rounded-lg transition-colors duration-150 ${theme === 'dark' ? 'hover:bg-gray-700 text-gray-400 hover:text-gray-200' : 'hover:bg-gray-100 text-gray-400 hover:text-gray-600'}`}
                aria-label="Close"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {(promptsConfigFetch === 'loading' || loadingData) && (
            <div className="h-0.5 w-full overflow-hidden shrink-0">
              <div
                className={`h-full ${theme === 'dark' ? 'bg-cyan-500' : 'bg-cyan-600'}`}
                style={{ animation: 'pgm-loading-bar 1.4s ease-in-out infinite' }}
              />
            </div>
          )}
          {promptsConfigFetch === 'error' && (
            <div className={`px-5 py-2 text-sm ${theme === 'dark' ? 'bg-red-900/30 text-red-100' : 'bg-red-50 text-red-900'}`}>
              Could not load <code className="text-xs">/api/config</code> (network, CORS, or wrong API URL in the build). The
              UI cannot tell whether prompt storage is enabled. Confirm <code className="text-xs">VITE_API_URL</code> matches
              this stack and open DevTools → Network for the config request.
            </div>
          )}
          {promptsConfigFetch === 'ok' && !promptsTableConfigured && (
            <div className={`px-5 py-2 text-sm ${theme === 'dark' ? 'bg-amber-900/30 text-amber-100' : 'bg-amber-50 text-amber-900'}`}>
              Prompt cloud storage is not configured on this environment (<code className="text-xs">PROMPTS_TABLE_NAME</code>
              ). Browse official prompts only; saving and community features require deploy.
            </div>
          )}
          {loadErr && <div className={`px-5 py-2 text-sm text-red-400`}>{loadErr}</div>}

          {tab === 'browse' && (
            <>
              <div className={`${isCompact ? 'p-3 space-y-2' : 'p-5 space-y-4'} border-b ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
                <div className="flex items-start gap-2">
                  <span className={`text-xs uppercase tracking-wide ${textMuted} shrink-0 mt-1.5`}>Tag</span>
                  <div className={`flex flex-wrap gap-2 items-center border rounded-md px-2 py-1.5 overflow-y-auto ${isCompact ? 'max-h-[70px]' : 'max-h-[102px]'} [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-gray-400/40 ${theme === "dark" ? "border-gray-600" : "border-gray-300"}`}>
                    <button
                      type="button"
                      onClick={() => {
                        setTagFilter(null);
                        window.clarity?.('event', 'prompt_gallery_tag_filter');
                        window.clarity?.('set', 'gallery_tag_filter', 'all');
                      }}
                      className={`h-6 inline-flex items-center px-2.5 rounded-full text-xs shrink-0 ${tagFilter === null ? chipOn : chipOff}`}
                    >
                      All
                    </button>
                    {allTags.map((t) => (
                      <button
                        key={t}
                        type="button"
                        onClick={() => {
                          setTagFilter((cur) => (cur === t ? null : t));
                          window.clarity?.('event', 'prompt_gallery_tag_filter');
                          window.clarity?.('set', 'gallery_tag_filter', t);
                        }}
                        className={`h-6 inline-flex items-center px-2.5 rounded-full text-xs shrink-0 ${tagFilter === t ? chipOn : chipOff}`}
                      >
                        {t}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 items-center">
                  <span className={`text-xs uppercase tracking-wide ${textMuted}`}>Source</span>
                  {SOURCE_FILTER_OPTIONS.map(({ id, label }) => (
                    <button
                      key={id}
                      type="button"
                      onClick={() => {
                        setSourceFilter(id);
                        window.clarity?.('event', 'prompt_gallery_source_filter');
                        window.clarity?.('set', 'gallery_source_filter', id);
                      }}
                      className={`px-2.5 py-1 rounded-full text-xs border ${sourceFilter === id
                          ? gallerySourceFilterSelectedClass(id, theme)
                          : `${chipOff} border-transparent`
                        }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                <div className="flex flex-wrap gap-3 items-center">
                  <label className={`text-sm ${textSecondary} flex items-center gap-2`}>
                    Assistant
                    <select
                      value={agentFilter}
                      onChange={(e) => {
                        setAgentFilter(e.target.value);
                        window.clarity?.('event', 'prompt_gallery_agent_filter');
                        window.clarity?.('set', 'gallery_agent_filter', e.target.value);
                      }}
                      className={`rounded-lg px-2 py-1.5 text-sm border ${inputBg}`}
                    >
                      <option value="all">All</option>
                      {agentOptions.map((a) => (
                        <option key={a.id} value={a.id}>
                          {a.name}
                        </option>
                      ))}
                    </select>
                  </label>
                  <input
                    type="search"
                    placeholder="Search prompts…"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    className={`flex-1 min-w-[160px] rounded-lg px-3 py-1.5 text-sm border ${inputBg}`}
                  />
                </div>
              </div>

              <div className={`flex-1 overflow-y-auto ${isCompact ? 'p-3' : 'p-5'} min-h-[200px] [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-gray-400/40`}>
                {loadingData ? (
                  <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 ${isCompact ? 'gap-2' : 'gap-4'}`}>
                    {Array.from({ length: 6 }).map((_, i) => (
                      <div
                        key={i}
                        className={`flex flex-col rounded-lg border p-4 ${theme === 'dark' ? 'border-gray-700 bg-gray-900/40' : 'border-gray-200 bg-gray-50'} animate-pulse`}
                      >
                        <div className="flex items-center justify-between gap-2 mb-3">
                          <div className={`h-4 rounded w-3/5 ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'}`} />
                          <div className={`h-4 rounded w-14 ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'}`} />
                        </div>
                        <div className={`h-3 rounded w-full mb-1.5 ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'}`} />
                        <div className={`h-3 rounded w-4/5 mb-1.5 ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'}`} />
                        <div className={`h-3 rounded w-2/3 mb-4 ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'}`} />
                        <div className="flex gap-1.5 mt-auto mb-3">
                          <div className={`h-4 rounded-full w-12 ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'}`} />
                          <div className={`h-4 rounded-full w-16 ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'}`} />
                        </div>
                        <div className="flex justify-between items-center mt-1">
                          <div className={`h-3 rounded w-24 ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'}`} />
                          <div className={`h-8 rounded-lg w-24 ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'}`} />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : mergedBrowseEntries.length === 0 ? (
                  <div className={`text-center py-16 ${textMuted}`}>
                    <p className={`text-lg font-medium ${textSecondary}`}>No prompts for your current access</p>
                  </div>
                ) : filtered.length === 0 ? (
                  <div className={`text-center py-16 ${textMuted}`}>
                    <p>No prompts match your filters.</p>
                  </div>
                ) : (
                  <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 ${isCompact ? 'gap-2' : 'gap-4'}`}>
                    {filtered.map((entry) => {
                      const agent = pickAgentForEntry(entry, agentsForPicker);
                      return (
                        <article
                          key={`${entry.source}:${entry.id}`}
                          className={`flex flex-col rounded-lg border ${isCompact ? 'p-3' : 'p-4'} ${cardBorder} ${theme === 'dark' ? 'bg-gray-900/40' : 'bg-gray-50'}`}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <h3 className={`font-semibold ${textPrimary} flex-1`}>{entry.title}</h3>
                            <span className={gallerySourceChipClasses(entry.source, theme)}>
                              {gallerySourceLabel(entry.source)}
                            </span>
                          </div>
                          <p className={`text-sm mt-2 flex-1 ${isCompact ? 'max-h-16' : 'max-h-24'} overflow-y-auto [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-gray-400/40 ${textSecondary}`}>{entry.body}</p>
                          <div className="flex flex-wrap gap-1.5 mt-3">
                            {entry.tags.map((t) => (
                              <span key={t} className={`text-[10px] px-2 py-0.5 rounded-full ${chipOff}`}>
                                {t}
                              </span>
                            ))}
                          </div>
                          <div className="mt-4 flex items-center justify-between gap-2">
                            <span className={`text-xs ${textMuted}`}>
                              {agent ? `Uses: ${agent.name}` : 'No matching assistant'}
                            </span>
                            <button
                              type="button"
                              disabled={!agent}
                              onClick={() => {
                                if (!agent) return;
                                onUsePrompt(entry.body, agent);
                                window.clarity?.('event', 'prompt_gallery_use_prompt');
                                window.clarity?.('set', 'gallery_prompt_source', entry.source);
                                window.clarity?.('set', 'gallery_agent_used', agent.id);
                              }}
                              className="text-sm px-3 py-1.5 rounded-lg bg-cyan-600 text-white hover:bg-cyan-500 disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                              Use prompt
                            </button>
                          </div>
                        </article>
                      );
                    })}
                  </div>
                )}
              </div>
            </>
          )}

          {tab === 'add' && (
            <div className={`${isCompact ? 'p-3 space-y-2' : 'p-5 space-y-4'} overflow-y-auto flex-1 [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-gray-400/40`}>
              {!accessToken.trim() && (
                <p className={`text-sm ${textMuted}`}>Sign in to add or edit prompts.</p>
              )}
              {!promptCloudReady && (
                <p className={`text-sm ${textMuted}`}>
                  {promptsConfigFetch === 'loading' && 'Checking prompt storage with the server…'}
                  {promptsConfigFetch === 'error' && 'Server configuration could not be loaded — form disabled.'}
                  {promptsConfigFetch === 'ok' && !promptsTableConfigured && 'Table not configured — form disabled.'}
                </p>
              )}
              <label className={`block text-sm ${textSecondary}`}>
                Title
                <input
                  value={formTitle}
                  onChange={(e) => setFormTitle(e.target.value)}
                  disabled={!promptCloudReady || !accessToken.trim()}
                  className={`mt-1 w-full rounded-lg px-3 py-2 text-sm border ${inputBg}`}
                />
              </label>
              <label className={`block text-sm ${textSecondary}`}>
                Prompt body
                <textarea
                  value={formBody}
                  onChange={(e) => setFormBody(e.target.value)}
                  disabled={!promptCloudReady || !accessToken.trim()}
                  rows={isCompact ? 4 : 6}
                  className={`mt-1 w-full rounded-lg px-3 py-2 text-sm border ${inputBg}`}
                />
              </label>
              <label className={`block text-sm ${textSecondary}`}>
                Tags (comma-separated)
                <input
                  value={formTags}
                  onChange={(e) => setFormTags(e.target.value)}
                  disabled={!promptCloudReady || !accessToken.trim()}
                  className={`mt-1 w-full rounded-lg px-3 py-2 text-sm border ${inputBg}`}
                />
              </label>
              <div>
                <p className={`text-sm ${textSecondary} mb-2`}>Suggested assistants</p>
                <div className="flex flex-wrap gap-3">
                  {agentsForPicker.map((a) => (
                    <label key={a.id} className={`flex items-center gap-2 text-sm ${textSecondary}`}>
                      <input
                        type="checkbox"
                        checked={!!formAgents[a.id]}
                        disabled={!promptCloudReady || !accessToken.trim()}
                        onChange={(e) => setFormAgents((prev) => ({ ...prev, [a.id]: e.target.checked }))}
                      />
                      {a.name}
                    </label>
                  ))}
                </div>
              </div>
              <div className="space-y-2">
                <p className={`text-sm ${textSecondary}`}>Visibility</p>
                <label className={`flex items-center gap-2 text-sm ${textSecondary}`}>
                  <input
                    type="radio"
                    name="share"
                    checked={formShare === 'personal'}
                    disabled={!promptCloudReady || !accessToken.trim()}
                    onChange={() => setFormShare('personal')}
                  />
                  Personal (only you, in gallery + My prompts)
                </label>
                <label className={`flex items-center gap-2 text-sm ${textSecondary}`}>
                  <input
                    type="radio"
                    name="share"
                    checked={formShare === 'global_request'}
                    disabled={!promptCloudReady || !accessToken.trim()}
                    onChange={() => setFormShare('global_request')}
                  />
                  Request sharing with everyone (admins must approve)
                </label>
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={saving || !promptCloudReady || !accessToken.trim()}
                  onClick={() => void submitForm()}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-cyan-600 text-white text-sm font-medium hover:bg-cyan-500 disabled:opacity-40 disabled:cursor-not-allowed transition-colors duration-150 shadow-sm"
                >
                  {saving ? (
                    <>
                      <svg className="animate-spin w-3.5 h-3.5 shrink-0" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      Saving…
                    </>
                  ) : editing ? (
                    <>
                      <svg className="w-3.5 h-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Z" />
                      </svg>
                      Update
                    </>
                  ) : (
                    <>
                      <svg className="w-3.5 h-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M17.593 3.322c1.1.128 1.907 1.077 1.907 2.185V21L12 17.25 4.5 21V5.507c0-1.108.806-2.057 1.907-2.185a48.507 48.507 0 0 1 11.186 0Z" />
                      </svg>
                      Save
                    </>
                  )}
                </button>
                {editing && (
                  <button
                    type="button"
                    onClick={() => {
                      setEditing(null);
                      setFormTitle('');
                      setFormBody('');
                      setFormTags('');
                      setTab('mine');
                      window.clarity?.('event', 'prompt_gallery_edit_cancelled');
                    }}
                    className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border text-sm font-medium transition-colors duration-150 ${theme === 'dark' ? 'border-gray-500 text-gray-300 hover:bg-gray-700 hover:text-white hover:border-gray-400' : 'border-gray-400 text-gray-600 hover:bg-gray-100 hover:text-gray-900 hover:border-gray-500'}`}
                  >
                    <svg className="w-3.5 h-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
                    </svg>
                    Cancel edit
                  </button>
                )}
              </div>
            </div>
          )}

          {tab === 'mine' && (
            <div className={`${isCompact ? 'p-3 space-y-2' : 'p-5 space-y-3'} overflow-y-auto flex-1 [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-gray-400/40`}>
              {!accessToken.trim() && <p className={textMuted}>Sign in to see saved prompts.</p>}
              {accessToken.trim() && (
                <div className="flex flex-wrap items-center gap-2">
                  {(['active', 'deleted'] as const).map((v) => (
                    <button
                      key={v}
                      type="button"
                      onClick={() => setMineView(v)}
                      className={`text-xs font-medium px-3 py-1.5 rounded-lg border transition-colors duration-150 ${
                        mineView === v
                          ? theme === 'dark'
                            ? 'bg-cyan-900/40 border-cyan-600/60 text-cyan-200'
                            : 'bg-cyan-50 border-cyan-400 text-cyan-800'
                          : theme === 'dark'
                            ? 'border-gray-600 text-gray-400 hover:bg-gray-800'
                            : 'border-gray-300 text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      {v === 'active' ? `Active (${mine.length})` : `Deleted (${deletedMine.length})`}
                    </button>
                  ))}
                  {mineView === 'deleted' && (
                    <p className={`text-xs ${textMuted}`}>
                      Soft-deleted personal prompts are kept for {retentionDays} days, then purged automatically.
                    </p>
                  )}
                </div>
              )}
              {loadingData && accessToken.trim() && (
                <div className="space-y-3">
                  {Array.from({ length: 4 }).map((_, i) => (
                    <div
                      key={i}
                      className={`rounded-lg border p-4 animate-pulse ${theme === 'dark' ? 'border-gray-700 bg-gray-900/30' : 'border-gray-200 bg-gray-50'}`}
                    >
                      <div className="flex justify-between gap-4 mb-3">
                        <div className="flex-1 space-y-2">
                          <div className={`h-4 rounded w-2/5 ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'}`} />
                          <div className={`h-3 rounded w-1/4 ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'}`} />
                        </div>
                        <div className="flex gap-1.5 shrink-0">
                          <div className={`h-7 rounded-lg w-20 ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'}`} />
                          <div className={`h-7 rounded-lg w-14 ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'}`} />
                          <div className={`h-7 rounded-lg w-12 ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'}`} />
                          <div className={`h-7 rounded-lg w-14 ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'}`} />
                        </div>
                      </div>
                      <div className={`h-3 rounded w-full mb-1.5 ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'}`} />
                      <div className={`h-3 rounded w-3/4 ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'}`} />
                    </div>
                  ))}
                </div>
              )}
              {!loadingData && accessToken.trim() && mineView === 'deleted' && deletedMine.length === 0 && (
                <p className={textMuted}>No deleted prompts.</p>
              )}
              {!loadingData && accessToken.trim() && mineView === 'deleted' && deletedMine.map((m) => {
                const isBusy = busyId === m.sk;
                const expiry = daysLeftHint(m);
                return (
                  <div
                    key={m.sk}
                    className={`rounded-lg border ${isCompact ? 'p-3' : 'p-4'} relative transition-opacity ${cardBorder} ${theme === 'dark' ? 'bg-gray-900/30' : 'bg-gray-50'} ${isBusy ? 'opacity-40 pointer-events-none' : ''}`}
                  >
                    <div className="flex flex-wrap justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className={`font-medium ${textPrimary}`}>{m.title}</p>
                        <p className={`text-xs mt-1 ${textMuted}`}>
                          Deleted
                          {m.deleted_at ? ` · ${String(m.deleted_at).slice(0, 19).replace('T', ' ')}` : ''}
                          {expiry ? ` · ${expiry}` : ''}
                        </p>
                      </div>
                      <div className="flex flex-wrap gap-1.5 shrink-0 justify-end items-center">
                        <button
                          type="button"
                          disabled={isBusy || !promptCloudReady}
                          onClick={() => void restoreMine(m)}
                          className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg bg-cyan-600 text-white hover:bg-cyan-500 disabled:opacity-40 disabled:cursor-not-allowed transition-colors duration-150"
                        >
                          Restore
                        </button>
                        <button
                          type="button"
                          disabled={isBusy || !promptCloudReady}
                          onClick={() => void purgeMine(m)}
                          className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg border border-red-700/60 text-red-400 hover:bg-red-900/40 hover:text-red-300 disabled:opacity-40 disabled:cursor-not-allowed transition-colors duration-150"
                        >
                          Permanently delete
                        </button>
                      </div>
                    </div>
                    <p className={`text-sm mt-2 ${isCompact ? 'max-h-12' : 'max-h-16'} overflow-y-auto [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-gray-400/40 ${textSecondary}`}>{m.body}</p>
                  </div>
                );
              })}
              {!loadingData && accessToken.trim() && mineView === 'active' && mine.length === 0 && (
                <p className={textMuted}>No saved prompts yet.</p>
              )}
              {!loadingData && mineView === 'active' && mine.map((m) => {
                const isShared = m.share_scope === 'peer_received';
                const isBusy = busyId === m.sk;
                const isSaving = savingId === m.sk;
                const useAgent = pickAgentForEntry(
                  { suggestedAgentIds: Array.isArray(m.suggested_agent_ids) ? m.suggested_agent_ids : [] },
                  agentsForPicker,
                );
                const isPanelOpen = sharingPromptId === m.id;
                return (
                  <div
                    key={m.sk}
                    className={`rounded-lg border ${isCompact ? 'p-3' : 'p-4'} relative transition-opacity ${cardBorder} ${theme === 'dark' ? 'bg-gray-900/30' : 'bg-gray-50'} ${isBusy ? 'opacity-40 pointer-events-none' : ''}`}
                  >
                    <div className="flex flex-wrap justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <p className={`font-medium ${textPrimary}`}>{m.title}</p>
                          {isShared && (
                            <span className={`text-[10px] px-2 py-0.5 rounded border font-medium shrink-0 ${theme === 'dark' ? 'bg-amber-900/50 text-amber-200 border-amber-500/50' : 'bg-amber-50 text-amber-800 border-amber-300'}`}>
                              Shared
                            </span>
                          )}
                        </div>
                        <p className={`text-xs mt-1 ${textMuted}`}>
                          {modLabel(m)}
                          {isShared && m.shared_by_email ? ` · from ${m.shared_by_email}` : m.share_scope === 'global_request' ? ' · Community path' : ' · Personal'}
                        </p>
                      </div>
                      <div className="flex flex-wrap gap-1.5 shrink-0 justify-end items-center">
                        <button
                          type="button"
                          disabled={!useAgent}
                          title={!useAgent ? 'No suggested assistant matches your access' : undefined}
                          onClick={() => {
                            if (!useAgent) return;
                            onUsePrompt(m.body, useAgent);
                            window.clarity?.('event', 'prompt_gallery_use_mine_prompt');
                            window.clarity?.('set', 'gallery_agent_used', useAgent.id);
                          }}
                          className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg bg-cyan-600 text-white hover:bg-cyan-500 disabled:opacity-40 disabled:cursor-not-allowed transition-colors duration-150"
                        >
                          <svg className="w-3 h-3 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5.25 5.653c0-.856.917-1.398 1.667-.986l11.54 6.347a1.125 1.125 0 0 1 0 1.972l-11.54 6.347a1.125 1.125 0 0 1-1.667-.986V5.653Z" />
                          </svg>
                          Use prompt
                        </button>
                        {isShared ? (
                          <>
                            <button
                              type="button"
                              disabled={isSaving || isBusy || !promptCloudReady}
                              className={`inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg border transition-colors duration-150 disabled:opacity-40 disabled:cursor-not-allowed ${theme === 'dark' ? 'border-gray-600 text-gray-300 hover:bg-gray-700 hover:text-white' : 'border-gray-300 text-gray-600 hover:bg-gray-100 hover:text-gray-900'}`}
                              onClick={() => void saveShared(m)}
                            >
                              {isSaving ? (
                                <>
                                  <svg className="animate-spin w-3 h-3 shrink-0" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                  </svg>
                                  Saving…
                                </>
                              ) : (
                                <>
                                  <svg className="w-3 h-3 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M17.593 3.322c1.1.128 1.907 1.077 1.907 2.185V21L12 17.25 4.5 21V5.507c0-1.108.806-2.057 1.907-2.185a48.507 48.507 0 0 1 11.186 0Z" />
                                  </svg>
                                  Save
                                </>
                              )}
                            </button>
                            <button
                              type="button"
                              disabled={isSaving || isBusy}
                              className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg border border-red-700/60 text-red-400 hover:bg-red-900/40 hover:text-red-300 disabled:opacity-40 disabled:cursor-not-allowed transition-colors duration-150"
                              onClick={() => void dismissShared(m)}
                            >
                              <svg className="w-3 h-3 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
                              </svg>
                              Dismiss
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              type="button"
                              disabled={isBusy || !promptCloudReady}
                              title="Share with a colleague"
                              onClick={() => openSharePanel(m.id)}
                              className={`inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg border transition-colors duration-150 disabled:opacity-40 disabled:cursor-not-allowed ${isPanelOpen
                                ? theme === 'dark' ? 'bg-cyan-900/40 border-cyan-600/60 text-cyan-300' : 'bg-cyan-50 border-cyan-400 text-cyan-700'
                                : theme === 'dark' ? 'border-gray-600 text-gray-300 hover:bg-gray-700 hover:text-white' : 'border-gray-300 text-gray-600 hover:bg-gray-100 hover:text-gray-900'}`}
                            >
                              <svg className="w-3 h-3 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M7.217 10.907a2.25 2.25 0 1 0 0 2.186m0-2.186c.18.324.283.696.283 1.093s-.103.77-.283 1.093m0-2.186 9.566-5.314m-9.566 7.5 9.566 5.314m0 0a2.25 2.25 0 1 0 3.935 2.186 2.25 2.25 0 0 0-3.935-2.186Zm0-12.814a2.25 2.25 0 1 0 3.933-2.185 2.25 2.25 0 0 0-3.933 2.185Z" />
                              </svg>
                              Share
                            </button>
                            <button
                              type="button"
                              className={`inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg border transition-colors duration-150 ${theme === 'dark' ? 'border-gray-600 text-gray-300 hover:bg-gray-700 hover:text-white' : 'border-gray-300 text-gray-600 hover:bg-gray-100 hover:text-gray-900'}`}
                              onClick={() => {
                                setEditing(m);
                                setTab('add');
                                window.clarity?.('event', 'prompt_gallery_edit_started');
                              }}
                            >
                              <svg className="w-3 h-3 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125" />
                              </svg>
                              Edit
                            </button>
                            <button
                              type="button"
                              className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg border border-red-700/60 text-red-400 hover:bg-red-900/40 hover:text-red-300 transition-colors duration-150"
                              onClick={() => void deleteMine(m)}
                            >
                              <svg className="w-3 h-3 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
                              </svg>
                              Delete
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                    <p className={`text-sm mt-2 ${isCompact ? 'max-h-12' : 'max-h-16'} overflow-y-auto [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-gray-400/40 ${textSecondary}`}>{m.body}</p>
                    {m.mod_pk === 'REJECTED' && m.review_note && (
                      <p className="text-xs text-amber-300 mt-2">Note: {String(m.review_note)}</p>
                    )}

                    {/* Share panel — only for personal prompts */}
                    {isPanelOpen && !isShared && (
                      <div className={`mt-3 pt-3 border-t ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
                        <p className={`text-xs font-medium mb-2 ${textSecondary}`}>Share with colleagues</p>
                        {/* Recipient chips */}
                        {shareRecipients.length > 0 && (
                          <div className="flex flex-wrap gap-1.5 mb-2">
                            {shareRecipients.map((email) => (
                              <span
                                key={email}
                                className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border ${theme === 'dark' ? 'bg-cyan-900/40 text-cyan-200 border-cyan-700' : 'bg-cyan-50 text-cyan-900 border-cyan-300'}`}
                              >
                                {email}
                                <button
                                  type="button"
                                  aria-label={`Remove ${email}`}
                                  onClick={() => removeShareRecipient(email)}
                                  className="ml-0.5 hover:text-red-400 focus:outline-none"
                                >
                                  ×
                                </button>
                              </span>
                            ))}
                          </div>
                        )}
                        {/* Search input with dropdown */}
                        <div className="relative flex gap-2">
                          <div className="relative flex-1 min-w-0">
                            <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
                            </svg>
                            <input
                              ref={shareInputRef}
                              type="text"
                              autoComplete="off"
                              placeholder="Search colleague by email…"
                              value={shareInput}
                              onChange={(e) => {
                                const v = e.target.value;
                                setShareInput(v);
                                setShareError(null);
                                setShareDropdownIdx(-1);
                                if (shareDebounceRef.current) clearTimeout(shareDebounceRef.current);
                                shareDebounceRef.current = setTimeout(() => {
                                  setShareDropdownOpen(v.trim().length > 0);
                                }, 150);
                              }}
                              onFocus={() => {
                                if (shareInput.trim()) setShareDropdownOpen(true);
                              }}
                              onBlur={() => {
                                // Delay close so click on dropdown option registers
                                setTimeout(() => setShareDropdownOpen(false), 150);
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Escape') {
                                  setShareDropdownOpen(false);
                                  setShareDropdownIdx(-1);
                                  return;
                                }
                                if (e.key === 'ArrowDown') {
                                  e.preventDefault();
                                  setShareDropdownIdx((i) => Math.min(i + 1, shareDropdownSuggestions.length - 1));
                                  setShareDropdownOpen(true);
                                  return;
                                }
                                if (e.key === 'ArrowUp') {
                                  e.preventDefault();
                                  setShareDropdownIdx((i) => Math.max(i - 1, -1));
                                  return;
                                }
                                if (e.key === 'Enter' || e.key === 'Tab') {
                                  e.preventDefault();
                                  if (shareDropdownIdx >= 0 && shareDropdownSuggestions[shareDropdownIdx]) {
                                    addShareRecipient(shareDropdownSuggestions[shareDropdownIdx]);
                                  } else if (shareInput.trim().includes('@')) {
                                    addShareRecipient(shareInput);
                                  }
                                }
                              }}
                              className={`w-full rounded-lg pl-8 pr-3 py-1.5 text-sm border ${inputBg}`}
                            />
                            {/* Dropdown suggestions */}
                            {shareDropdownOpen && shareDropdownSuggestions.length > 0 && (
                              <ul className={`absolute z-50 left-0 right-0 top-full mt-1 rounded-lg border shadow-lg overflow-hidden ${theme === 'dark' ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'}`}>
                                {shareDropdownSuggestions.map((email, idx) => (
                                  <li key={email}>
                                    <button
                                      type="button"
                                      onMouseDown={(e) => e.preventDefault()}
                                      onClick={() => addShareRecipient(email)}
                                      className={`w-full text-left px-3 py-2 text-xs truncate transition-colors ${idx === shareDropdownIdx
                                        ? theme === 'dark' ? 'bg-cyan-700/40 text-cyan-100' : 'bg-cyan-50 text-cyan-900'
                                        : theme === 'dark' ? 'text-gray-200 hover:bg-gray-700' : 'text-gray-800 hover:bg-gray-50'
                                      }`}
                                    >
                                      {email}
                                    </button>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                          <button
                            type="button"
                            disabled={shareRecipients.length === 0 || shareStatus === 'sending'}
                            onClick={() => void submitShare(m)}
                            className="px-3 py-1.5 text-xs rounded-lg bg-cyan-600 text-white hover:bg-cyan-500 disabled:opacity-40 shrink-0"
                          >
                            {shareStatus === 'sending' ? 'Sending…' : `Send${shareRecipients.length > 0 ? ` (${shareRecipients.length})` : ''}`}
                          </button>
                        </div>
                        {shareError && <p className="text-xs text-red-400 mt-1">{shareError}</p>}
                        {shareStatus === 'sent' && (
                          <p className="text-xs text-emerald-400 mt-1">
                            Shared with {shareRecipients.length} colleague{shareRecipients.length !== 1 ? 's' : ''}.
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
        </div>
      </div>
      {confirmDialog}
      {noticeDialog}
      {toastPortal}
    </>,
    document.body,
  );
};
